import React, { useState } from "react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { Form, LwSso, SubmitButton, Title } from "./styled/login";
import {
  StyledSelectionBox,
  StyledSelectionBoxOption,
  MultipleAccountsMessage,
  AccountName,
  AccountProvider,
  AccountDetails,
  AccountActivity,
  AccountIcon,
  RoleBadge
} from "./styled/account-picker";
import { HelixIcon } from "@helix/helix-icon";
import { 
  user as UserIcon, 
  wolfie as WolfieIcon,
  building_modern as BuildingIcon,
  building_bank as BankIcon,
  home as HomeIcon 
} from "@helix/helix-icon/outlined";
import { pluralize } from '@wrstudios/utils'
import { LwSsoIcon } from "./icons";

function helixIcon(icon) {
  return function Icon({ className }) {
    return <HelixIcon icon={icon} className={className} />
  }
}

const accountTypes = {
  agent_bundle: { label: 'Paid by you', Icon: helixIcon(UserIcon) },
  affiliate_store: { label: 'Partner account', Icon: helixIcon(HomeIcon) },
  legacy_single: { label: 'Paid by you', Icon: helixIcon(UserIcon) },
  platform_ecommerce: { label: 'Paid by you', Icon: helixIcon(UserIcon) },
  platform_client_brokerage: {
    label: 'Lone Wolf Platform account',
    Icon: helixIcon(WolfieIcon),
    iconClassName: 'helix-text-green-600',
  },
  legacy_small_broker: {
    label: 'Provided by your brokerage',
    Icon: helixIcon(BuildingIcon)
  },
  mls_site_license: {
    label: 'Provided by your MLS',
    Icon: helixIcon(BankIcon)
  },
  v2_broker: {
    label: 'Provided by your brokerage',
    Icon: helixIcon(BuildingIcon)
  }
}

const defaultAccountType = {
  label: 'Personal account',
  Icon: helixIcon(UserIcon)
}


function lastReportLabel({ totalReports, lastReportCreatedAt }) {
  if (!totalReports || !lastReportCreatedAt) return "No reports yet";

  return `Last report ${formatDistanceToNow(parseISO(lastReportCreatedAt), {
    addSuffix: true
  })}`;
}

function AccountPicker({ lwSsoUrl, email, accounts = [], onContinue }) {
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const account = accounts.find((option) => String(option?.accountId) === selectedAccount?.id);
  const isPlatformClientBrokerage = !!account?.platformSignInRequired

  const handleContinue = (event) => {
    event.preventDefault();

    if (!selectedAccount || submitting) {
      return;
    }

    if(isPlatformClientBrokerage) {
      setSubmitting(true);
      window.location.href = lwSsoUrl;
      return;
    }

    if (onContinue) {
      setSubmitting(true);
      onContinue(account);
    }
  };

  return (
    <Form method="post" onSubmit={handleContinue}>
      <Title>Select an account</Title>
      <MultipleAccountsMessage>
        We found more than one Cloud CMA account associated with{" "}
        <strong>{email}</strong>. Select the account you want to sign in with.
      </MultipleAccountsMessage>
      <StyledSelectionBox
        selectionMode="single"
        value={selectedAccount ?? undefined}
        onChange={(selected) => setSelectedAccount(selected)}>
        {accounts.map((account) => {
          const logo = account.accountLogo || account.userLogo;
          const { label: providerLabel, Icon: ProviderIcon, iconClassName } =
            accountTypes[account.accountType] || defaultAccountType;
          
          return (
            <StyledSelectionBoxOption
              key={account.accountId}
              id={String(account.accountId)}
              value={String(account.accountId)}>
              <AccountIcon>
                {logo ? (
                  <img src={logo} alt="" />
                ) : (
                  <ProviderIcon width={20} height={20} className={iconClassName} />
                )}
              </AccountIcon>
              <AccountDetails>
                <AccountName>{account.name}</AccountName>
                <AccountProvider>
                  {providerLabel}
                  {account.role === "Admin" && <RoleBadge>Admin</RoleBadge>}
                </AccountProvider>
                <AccountActivity>
                  {!!account.totalReports && (
                    <>
                      <p>{account.totalReports}{" "}{pluralize("report", account.totalReports || 0)}</p>
                      <span>•</span>
                    </>
                  )}
                  <p>{lastReportLabel(account)}</p>
                </AccountActivity>
              </AccountDetails>
            </StyledSelectionBoxOption>
          );
        })}
      </StyledSelectionBox>
      {!selectedAccount || !isPlatformClientBrokerage ? (
        <SubmitButton
          type="button"
          disabled={!selectedAccount || submitting}
          onClick={handleContinue}>
          Continue
        </SubmitButton>
      ) : (
        <LwSso href={lwSsoUrl} className="btn-md">
          <span className="sso-icon">
            <LwSsoIcon />
          </span>
          <span className="sso-text">Sign in with Lone Wolf Account</span>
        </LwSso>  
      )}
    </Form>
  );
}

export default AccountPicker;
